import type { HardwareColor, HardwareType } from "../model/Project";

/** Hex fills used to render each colour family. Each entry is the **face**
 *  colour; the rim shade is derived per-render by darkening it. */
export const HARDWARE_COLORS: Record<HardwareColor, string> = {
  silver: "#C0C0C0",
  brass: "#B5A642",
  "antiqued-brass": "#8B7355",
  black: "#1A1A1A",
  "gun-metal": "#2C3539",
};

/** Display labels for the colour swatch chips in the properties panel. */
export const HARDWARE_COLOR_LABELS: Record<HardwareColor, string> = {
  silver: "Silver",
  brass: "Brass",
  "antiqued-brass": "Antiqued Brass",
  black: "Black",
  "gun-metal": "Gun metal",
};

/** Display labels for the type gallery tiles. */
export const HARDWARE_TYPE_LABELS: Record<HardwareType, string> = {
  rivet: "Rivet",
  snap: "Snap",
  "magnetic-snap": "Magnetic snap",
  eyelet: "Eyelet",
  "concho-round": "Concho — round",
  "concho-star": "Concho — star",
  "concho-floral": "Concho — floral",
};

/** Common sizes (cap diameter in mm) for each hardware type. The size
 *  picker filters its preset chips by the active type. Values come from
 *  standard leathercraft suppliers (Tandy / Goldstar conventions). */
export const HARDWARE_SIZES_MM: Record<
  HardwareType,
  ReadonlyArray<{ label: string; mm: number }>
> = {
  rivet: [
    { label: "7 mm", mm: 7 },
    { label: "9 mm", mm: 9 },
    { label: "11 mm", mm: 11 },
  ],
  snap: [
    { label: "Line 16 (12.5 mm)", mm: 12.5 },
    { label: "Line 20 (15 mm)", mm: 15 },
    { label: "Line 24 (17 mm)", mm: 17 },
  ],
  "magnetic-snap": [
    { label: "14 mm", mm: 14 },
    { label: "18 mm", mm: 18 },
  ],
  eyelet: [
    { label: "5 mm", mm: 5 },
    { label: "6 mm", mm: 6 },
    { label: "8 mm", mm: 8 },
    { label: "10 mm", mm: 10 },
  ],
  "concho-round": [
    { label: "20 mm", mm: 20 },
    { label: "25 mm", mm: 25 },
    { label: "30 mm", mm: 30 },
  ],
  "concho-star": [
    { label: "20 mm", mm: 20 },
    { label: "25 mm", mm: 25 },
    { label: "30 mm", mm: 30 },
  ],
  "concho-floral": [
    { label: "20 mm", mm: 20 },
    { label: "25 mm", mm: 25 },
    { label: "30 mm", mm: 30 },
  ],
};

/** Default size for each type — the middle / most common option from the
 *  size lists above. Used when the user first picks a type from the
 *  gallery. */
export const HARDWARE_DEFAULT_SIZE_MM: Record<HardwareType, number> = {
  rivet: 9,
  snap: 15,
  "magnetic-snap": 14,
  eyelet: 6,
  "concho-round": 25,
  "concho-star": 25,
  "concho-floral": 25,
};

/** Diameter of the post / barrel that pierces the leather, expressed as a
 *  fraction of the visible cap diameter. Used when the Hardware tool
 *  auto-drills a hole on bare leather (clicked off any existing hole) so
 *  the new hole is plausibly sized for the picked hardware. The drilled
 *  hole *radius* on the `Hole` data model is half this value. */
export const HARDWARE_POST_RATIO: Record<HardwareType, number> = {
  rivet: 0.35,
  snap: 0.35,
  "magnetic-snap": 0.6,
  eyelet: 0.55,
  "concho-round": 0.2,
  "concho-star": 0.2,
  "concho-floral": 0.2,
};

/** Compute a sensible drilled-hole *radius* in mm for a given hardware
 *  type + cap diameter. Floored at 0.75 mm so we never produce a hole
 *  too small to see at the smallest hardware sizes. */
export function holeRadiusForHardware(
  type: HardwareType,
  capDiameterMm: number,
): number {
  const ratio = HARDWARE_POST_RATIO[type];
  return Math.max(0.75, (capDiameterMm * ratio) / 2);
}
